import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from '../../../main/services/toast';
import { ExportTemplate } from '../../models/export-template/export-template.model';
import { ExportTemplateService } from '../../services/export-template/export-template.service';

@Component({
  selector: 'jft-export-template-picker-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="modal-header">
      <h5 class="modal-title">
        <i class="fas fa-sliders-h me-2"></i>Advanced Data Export
      </h5>
      <button type="button" class="btn-close" (click)="close()"></button>
    </div>
    <div class="modal-body">
      <p class="text-muted mb-3">Select a template to export bid data for <strong>{{ tenderName }}</strong></p>

      <div *ngIf="loading" class="text-center py-4">
        <span class="spinner-border text-primary" role="status"></span>
        <p class="text-muted mt-2 mb-0">Loading templates...</p>
      </div>

      <div *ngIf="!loading && templates.length === 0" class="text-center py-4">
        <i class="fas fa-folder-open fa-2x text-muted mb-2"></i>
        <p class="text-muted mb-2">No export templates found.</p>
        <a href="/export-templates/new" class="btn btn-sm btn-outline-primary" (click)="close()">
          <i class="fas fa-plus me-1"></i>Create Template
        </a>
      </div>

      <div *ngIf="!loading && templates.length > 0" class="template-list">
        <div *ngFor="let template of templates"
             class="template-item d-flex align-items-center justify-content-between p-3 mb-2 rounded border">
          <div class="flex-grow-1">
            <div class="fw-semibold">{{ template.name }}</div>
            <small class="text-muted">
              {{ template.field_config.length }} fields
              <span *ngIf="template.description"> &middot; {{ template.description }}</span>
            </small>
          </div>
          <button class="btn btn-sm btn-primary ms-3"
                  [disabled]="requestedIds.has(template.id)"
                  (click)="download(template)">
            <span *ngIf="!requestedIds.has(template.id)">
              <i class="fas fa-download me-1"></i>Download
            </span>
            <span *ngIf="requestedIds.has(template.id)">
              <i class="fas fa-check me-1"></i>Requested
            </span>
          </button>
        </div>
      </div>
    </div>
    <div class="modal-footer">
      <a href="/export-templates" class="btn btn-outline-primary btn-sm" (click)="close()">
        <i class="fas fa-cog me-1"></i>Manage Templates
      </a>
      <button type="button" class="btn btn-primary btn-sm" (click)="close()">Close</button>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    .template-item {
      background-color: var(--card-bg, #fff);
      border-color: var(--border-color, #dee2e6) !important;
      transition: background-color 0.15s ease;
      &:hover { background-color: var(--table-hover-bg, #f8f9fa); }
    }
    .template-item .fw-semibold {
      color: var(--heading-color, inherit);
    }
  `]
})
export class ExportTemplatePickerModalComponent implements OnInit {
  // Injected via componentInstance in the parent
  tenderUid = '';
  tenderName = '';

  templates: ExportTemplate[] = [];
  loading = true;
  requestedIds = new Set<number>();

  constructor(
    private activeModal: NgbActiveModal,
    private exportTemplateService: ExportTemplateService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.exportTemplateService.getTemplates().subscribe({
      next: (templates) => {
        this.templates = templates;
        this.loading = false;
      },
      error: () => {
        this.toast.error('Failed to load export templates.', 'Error');
        this.loading = false;
      }
    });
  }

  download(template: ExportTemplate): void {
    this.requestedIds.add(template.id);
    this.exportTemplateService.requestExportAsync(this.tenderUid, template.id).subscribe({
      next: () => {
        this.toast.info(
          `Export "${template.name}" started. You'll be notified when the file is ready for download.`
        );
      },
      error: () => {
        this.requestedIds.delete(template.id);
        this.toast.error('Failed to start export. Please try again.', 'Error');
      }
    });
  }

  close(): void {
    this.activeModal.dismiss();
  }
}
